import React from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { MuiOtpInput } from "mui-one-time-password-input";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import PasswordTextField from "@/components/PasswordTextField";

import ResendButton from "./components/ResendButton";
import { FormValues } from "./LoginPage";
import nutricoachLogo from "../../../assets/Nutricoach.png";

const useStyles = makeStyles()((theme: Theme) => ({
  card: {
    minHeight: "40vh",
    minWidth: "40vw",
    paddingInline: "24px",
    paddingBlock: "12px",
  },
  forgotPassword: {
    textAlign: "right",
  },
  logo: {
    objectFit: "cover",
    width: "248px",
    marginBottom: "24px",
  },
  loader: {
    position: "absolute",
    top: 0,
    right: 0,
    left: 0,
  },
  link: {
    textDecoration: "none",
    color: "inherit",
  },
  muiOtp: {
    width: "40vw",
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  input: {
    /*Hide arrows */
    /* Chrome, Safari, Edge, Opera */
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      display: "none",
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  },
}));

type Props = {
  isLookUpSuccess: boolean;
  onResend: VoidFunction;
};

const LoginForm: React.FC<Props> = ({ isLookUpSuccess, onResend }) => {
  const { classes } = useStyles();
  const {
    control,
    register,
    formState: { errors, isSubmitting },
  } = useFormContext<FormValues>();

  return (
    <Box
      height="100vh"
      width="100vw"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      {isSubmitting && <LinearProgress className={classes.loader} />}
      <img src={nutricoachLogo} className={classes.logo} />
      <Card className={classes.card}>
        <CardContent>
          {isLookUpSuccess ? (
            <>
              <Typography variant="h5">OTP Verification</Typography>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                gutterBottom
              >
                We have sent the OTP code to your email.
              </Typography>
              <Box
                marginTop="32px"
                minHeight="96px"
                width="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="space-between"
              >
                <Controller
                  control={control}
                  name="otp_attempt"
                  render={({ field: { onChange, value } }) => (
                    <MuiOtpInput
                      className={classes.muiOtp}
                      value={value}
                      onChange={onChange}
                      length={6}
                      TextFieldsProps={{
                        error: !!errors.otp_attempt,
                        helperText: errors.otp_attempt?.message ?? " ",
                        type: "number",
                        required: true,
                        placeholder: "-",
                        className: classes.input,
                      }}
                    />
                  )}
                />
                <ResendButton onResend={onResend} delay={30} />
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h5">Welcome</Typography>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                gutterBottom
              >
                Login to your account and continue with us
              </Typography>
              <Box
                marginTop="32px"
                minHeight="96px"
                width="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="space-between"
              >
                <TextField
                  {...register("email")}
                  error={!!errors.email}
                  helperText={errors.email?.message ?? " "}
                  placeholder="Email"
                  type="email"
                  required
                  fullWidth
                />
                <PasswordTextField
                  {...register("password")}
                  error={!!errors.password}
                  helperText={errors.password?.message ?? " "}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  fullWidth
                />
              </Box>
              <Box
                display="flex"
                alignItems="flex-end"
                justifyContent="space-between"
              >
                <Typography variant="caption">
                  {"Don't have an account? "}
                  <strong>
                    <Link to="/register" className={classes.link}>
                      Register
                    </Link>
                  </strong>
                </Typography>
                <Typography
                  className={classes.forgotPassword}
                  variant="subtitle2"
                  color="textSecondary"
                >
                  <Link to="/forgot-password" className={classes.link}>
                    Forgot password?
                  </Link>
                </Typography>
              </Box>
            </>
          )}
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            marginTop="32px"
          >
            <Box width="40%" marginBottom="32px">
              <Button
                disabled={isSubmitting}
                size="large"
                type="submit"
                fullWidth
              >
                Login
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginForm;
